export interface UncertainValue {
  value: number;
  uncertainty: number;
}

function validateUncertainValue(measurement: UncertainValue): void {
  if (!Number.isFinite(measurement.value) || !Number.isFinite(measurement.uncertainty) || measurement.uncertainty < 0) {
    throw new TypeError('Measurement values must be finite and uncertainty must be non-negative.');
  }
}

export function propagateAddition(first: UncertainValue, second: UncertainValue): UncertainValue {
  validateUncertainValue(first); validateUncertainValue(second);
  return { value: first.value + second.value, uncertainty: first.uncertainty + second.uncertainty };
}

export function propagateSubtraction(first: UncertainValue, second: UncertainValue): UncertainValue {
  validateUncertainValue(first); validateUncertainValue(second);
  return { value: first.value - second.value, uncertainty: first.uncertainty + second.uncertainty };
}

export function propagateMultiplication(first: UncertainValue, second: UncertainValue): UncertainValue {
  validateUncertainValue(first); validateUncertainValue(second);
  const value = first.value * second.value;
  if (first.value === 0 || second.value === 0) {
    return { value, uncertainty: Math.abs(second.value) * first.uncertainty + Math.abs(first.value) * second.uncertainty };
  }
  const fractional = first.uncertainty / Math.abs(first.value) + second.uncertainty / Math.abs(second.value);
  return { value, uncertainty: Math.abs(value) * fractional };
}

export function propagateDivision(numerator: UncertainValue, denominator: UncertainValue): UncertainValue {
  validateUncertainValue(numerator); validateUncertainValue(denominator);
  if (denominator.value === 0) throw new RangeError('Cannot divide by a measured value of zero.');
  const value = numerator.value / denominator.value;
  if (numerator.value === 0) return { value, uncertainty: numerator.uncertainty / Math.abs(denominator.value) };
  const fractional = numerator.uncertainty / Math.abs(numerator.value) + denominator.uncertainty / Math.abs(denominator.value);
  return { value, uncertainty: Math.abs(value) * fractional };
}

export function propagatePower(measurement: UncertainValue, exponent: number): UncertainValue {
  validateUncertainValue(measurement);
  if (!Number.isFinite(exponent)) throw new TypeError('Exponent must be finite.');
  const value = measurement.value ** exponent;
  if (measurement.value === 0) {
    if (exponent < 1) throw new RangeError('Uncertainty propagation is undefined at zero for this exponent.');
    return { value, uncertainty: exponent === 1 ? measurement.uncertainty : 0 };
  }
  return { value, uncertainty: Math.abs(value) * Math.abs(exponent) * measurement.uncertainty / Math.abs(measurement.value) };
}

export function uncertaintyOfMean(values: number[]): UncertainValue {
  if (values.length < 2 || values.some((value) => !Number.isFinite(value))) {
    throw new RangeError('At least two finite readings are required.');
  }
  const mean = values.reduce((total, value) => total + value, 0) / values.length;
  const variance = values.reduce((total, value) => total + (value - mean) ** 2, 0) / (values.length - 1);
  return { value: mean, uncertainty: Math.sqrt(variance / values.length) };
}

export function calculateDensity(mass: UncertainValue, volume: UncertainValue): UncertainValue {
  return propagateDivision(mass, volume);
}